/**
 * Detalle del Plan HS: Cabecera + PDF Plan validado + Grid de acciones con
 * subida de respaldos PDF (máx 2 MB) por día, y % de cumplimiento en tiempo real.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../utils/format';
import { ArrowLeft, FileText, Upload, Trash2, ChevronDown, ChevronRight, Download, Calendar, Edit3, Save, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EsePlanHsDetalle() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const isHs = user?.rol === 'equipo_hs' || user?.rol === 'admin' || user?.rol === 'aprobador';
  const rutaBase = isHs ? '/equipo-hs/planes-hs' : '/ese/planes-hs';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/planes-hs/${id}`, { headers });
      setPlan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando plan');
      navigate(rutaBase);
    } finally { setLoading(false); }
  }, [id, headers, navigate, rutaBase]);

  useEffect(() => { load(); }, [load]);

  const uploadPlanPdf = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('El PDF supera los 2 MB'); return; }
    if (file.type !== 'application/pdf') { toast.error('Sólo PDF'); return; }
    setUploadingPlan(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/planes-hs/${id}/plan-pdf`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Plan PDF cargado');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir plan');
    } finally { setUploadingPlan(false); }
  };

  if (loading || !plan) {
    return <div className="p-8 text-center text-slate-500">Cargando...</div>;
  }

  const pct = plan.cumplimiento?.porcentaje ?? 0;
  const pctColor = pct >= 90 ? 'emerald' : pct >= 60 ? 'amber' : 'rose';

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
        <Link to={rutaBase} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-800">
          <ArrowLeft className="w-4 h-4" /> Volver a Planes HS
        </Link>

        {/* Cabecera */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between gap-6">
            <div className="flex-1">
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">{plan.numero_contrato_ost}</p>
              <h1 className="text-2xl font-bold text-slate-800 mt-1">{plan.nombre}</h1>
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-600">
                {plan.gerencia_nombre && <span><strong>Gerencia:</strong> {plan.gerencia_nombre}</span>}
                {plan.superintendencia_nombre && <span><strong>Superintendencia:</strong> {plan.superintendencia_nombre}</span>}
                <span><Calendar className="inline w-3 h-3 mr-1" /> {fmtDate(plan.periodo_inicio)} → {fmtDate(plan.periodo_fin)}</span>
                {plan.empresa && <span><strong>Empresa:</strong> {plan.empresa.razon_social}</span>}
              </div>
            </div>
            {/* Cumplimiento */}
            <div className="text-right shrink-0">
              <p className={`text-4xl font-bold text-${pctColor}-600`}>{pct}%</p>
              <p className="text-xs text-slate-500">
                {plan.cumplimiento?.entregados || 0} / {plan.cumplimiento?.requeridos || 0} respaldos
              </p>
            </div>
          </div>

          {/* PDF Plan validado */}
          <div className="mt-5 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Plan HS validado (PDF)</p>
                  <p className="text-xs text-slate-500">
                    Presentación PDF con nombre "{plan.nombre} — {plan.empresa?.razon_social || 'EMPRESA'}", validado por ADC LB. Máx 2 MB.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {plan.plan_pdf?.url && (
                  <a href={plan.plan_pdf.url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-xs text-slate-700 hover:bg-slate-100">
                    <Download className="w-3 h-3" /> Ver PDF
                  </a>
                )}
                {isHs && (
                  <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold cursor-pointer ${uploadingPlan ? 'opacity-60 pointer-events-none' : ''}`} data-testid="btn-upload-plan-pdf">
                    <Upload className="w-3 h-3" /> {plan.plan_pdf ? 'Reemplazar' : 'Subir PDF'}
                    <input type="file" accept="application/pdf" className="hidden" disabled={uploadingPlan}
                      onChange={e => { uploadPlanPdf(e.target.files?.[0]); e.target.value = ''; }} />
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Acciones mínimas */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Acciones mínimas del plan</h2>
          <p className="text-xs text-slate-500 mb-5">
            {isHs
              ? 'Configura frecuencia y período por acción. La ESE contratista sube los respaldos PDF diarios.'
              : 'Sube respaldos PDF (máx 2 MB) por día para las acciones con frecuencia diaria. Cada respaldo incrementa el % de cumplimiento.'}
          </p>
          <div className="space-y-3">
            {plan.acciones.map(a => (
              <AccionCard
                key={a.id}
                accion={a}
                respaldos={plan.respaldos.filter(r => r.accion_id === a.id)}
                planId={plan.id}
                headers={headers}
                onChanged={load}
                isHs={isHs}
                cumplDetalle={plan.cumplimiento?.detalle?.find(d => d.accion_id === a.id)}
              />
            ))}
          </div>
        </div>
    </div>
  );
}

function AccionCard({ accion, respaldos, planId, headers, onChanged, cumplDetalle, isHs }) {
  const [uploading, setUploading] = useState(false);
  const [selDate, setSelDate] = useState(new Date().toISOString().slice(0, 10));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({
    frecuencia: accion.frecuencia,
    periodo_inicio: accion.periodo_inicio,
    periodo_fin: accion.periodo_fin,
  });

  useEffect(() => {
    setEdit({
      frecuencia: accion.frecuencia,
      periodo_inicio: accion.periodo_inicio,
      periodo_fin: accion.periodo_fin,
    });
  }, [accion.frecuencia, accion.periodo_inicio, accion.periodo_fin]);

  // Generar lista de días del periodo (para grid visual)
  const dias = useMemo(() => {
    const out = [];
    const d0 = new Date(accion.periodo_inicio + 'T00:00');
    const d1 = new Date(accion.periodo_fin + 'T00:00');
    for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [accion.periodo_inicio, accion.periodo_fin]);

  const respByDate = useMemo(() => {
    const m = {};
    respaldos.forEach(r => { (m[r.fecha] = m[r.fecha] || []).push(r); });
    return m;
  }, [respaldos]);

  const handleUpload = async (file, fecha) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Máx 2 MB'); return; }
    if (file.type !== 'application/pdf') { toast.error('Sólo PDF'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('fecha', fecha);
      await axios.post(`${API}/planes-hs/${planId}/acciones/${accion.id}/respaldos`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Respaldo cargado');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al subir');
    } finally { setUploading(false); }
  };

  const handleDelete = async (rid) => {
    if (!window.confirm('¿Eliminar este respaldo?')) return;
    try {
      await axios.delete(`${API}/planes-hs/respaldos/${rid}`, { headers });
      toast.success('Respaldo eliminado');
      onChanged();
    } catch { toast.error('Error al eliminar'); }
  };

  const abrirRespaldo = async (archivo) => {
    if (!archivo?.bucket || !archivo?.path) return;
    try {
      const r = await axios.post(
        `${API}/storage/signed-url`,
        { bucket: archivo.bucket, path: archivo.path },
        { headers },
      );
      window.open(r.data.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo abrir el respaldo');
    }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/planes-hs/acciones/${accion.id}`, edit, { headers });
      toast.success('Acción actualizada');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
  };

  const isDiaria = accion.frecuencia === 'diaria';
  const pct = cumplDetalle?.porcentaje;

  return (
    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
      {/* Cabecera clickeable para colapsar */}
      <button type="button" onClick={() => setOpen(v => !v)}
        data-testid={`accion-toggle-${accion.orden}`}
        className="w-full flex items-start justify-between gap-4 p-4 hover:bg-slate-50 text-left">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[10px] font-bold text-white bg-emerald-600 px-2 py-0.5 rounded-full">#{accion.orden}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isDiaria ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                {isDiaria ? 'Frecuencia diaria' : 'Cuando ocurra'}
              </span>
              <span className="text-[10px] text-slate-500">Responsable: <strong>{accion.responsable}</strong></span>
              <span className="text-[10px] text-slate-400">{fmtDate(accion.periodo_inicio)} → {fmtDate(accion.periodo_fin)}</span>
            </div>
            <p className="font-semibold text-sm text-slate-800">{accion.titulo}</p>
            {open && <p className="text-xs text-slate-500 mt-1">{accion.descripcion}</p>}
          </div>
        </div>
        <div className="text-right shrink-0">
          {isDiaria && pct !== undefined && (
            <>
              <p className="text-lg font-bold text-slate-800">{pct}%</p>
              <p className="text-[10px] text-slate-500">{cumplDetalle?.entregados}/{cumplDetalle?.requeridos}</p>
            </>
          )}
        </div>
      </button>

      {/* Contenido expandido */}
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 bg-slate-50/50">
          {/* Panel de edición HS */}
          {isHs && (
            <div className="mb-3 flex items-center justify-between">
              {editing ? (
                <div className="flex items-center gap-2 flex-wrap w-full">
                  <label className="text-[11px] text-slate-600">Frecuencia:
                    <select value={edit.frecuencia} onChange={e => setEdit({ ...edit, frecuencia: e.target.value })}
                      className="ml-2 border border-slate-300 rounded px-2 py-1 text-xs">
                      <option value="diaria">Diaria</option>
                      <option value="cuando_ocurra">Cuando ocurra</option>
                    </select>
                  </label>
                  <label className="text-[11px] text-slate-600">Inicio:
                    <input type="date" value={edit.periodo_inicio} onChange={e => setEdit({ ...edit, periodo_inicio: e.target.value })}
                      className="ml-2 border border-slate-300 rounded px-2 py-1 text-xs" />
                  </label>
                  <label className="text-[11px] text-slate-600">Fin:
                    <input type="date" value={edit.periodo_fin} onChange={e => setEdit({ ...edit, periodo_fin: e.target.value })}
                      className="ml-2 border border-slate-300 rounded px-2 py-1 text-xs" />
                  </label>
                  <button onClick={saveEdit} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold">
                    <Save className="w-3 h-3" /> Guardar
                  </button>
                  <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-700 text-[11px]">
                    <X className="w-3 h-3" /> Cancelar
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-800 font-semibold">
                  <Edit3 className="w-3 h-3" /> Editar frecuencia / período
                </button>
              )}
            </div>
          )}

          {/* Barra de días con estado */}
          {isDiaria && (
            <div className="mt-2">
              <div className="flex flex-wrap gap-1 mb-2">
                {dias.map(d => {
                  const has = respByDate[d]?.length > 0;
                  return (
                    <div key={d} title={`${fmtDate(d)}${has ? ' - Respaldado' : ' - Pendiente'}`}
                      className={`w-6 h-6 rounded flex items-center justify-center text-[9px] font-bold ${has ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                      {has ? '✓' : d.slice(-2)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Subir respaldo (ESE + HS) */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <input type="date" value={selDate} onChange={e => setSelDate(e.target.value)}
              min={accion.periodo_inicio} max={accion.periodo_fin}
              className="border border-slate-300 rounded-lg px-2 py-1.5 text-xs" />
            <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold cursor-pointer ${uploading ? 'opacity-60 pointer-events-none' : ''}`} data-testid={`btn-respaldo-${accion.orden}`}>
              <Upload className="w-3 h-3" /> {uploading ? 'Subiendo...' : 'Subir respaldo PDF'}
              <input type="file" accept="application/pdf" className="hidden" disabled={uploading}
                onChange={e => { handleUpload(e.target.files?.[0], selDate); e.target.value = ''; }} />
            </label>
          </div>

          {/* Lista de respaldos cargados */}
          {respaldos.length > 0 && (
            <div className="mt-3 divide-y divide-slate-200 border-t border-slate-200 pt-2">
              {respaldos.map(r => (
                <div key={r.id} className="flex items-center justify-between py-1.5 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span className="font-semibold text-slate-700">{fmtDate(r.fecha)}</span>
                    <span className="text-slate-500 truncate">{r.archivo?.filename || 'respaldo.pdf'}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.archivo?.bucket && r.archivo?.path && (
                      <button type="button" onClick={() => abrirRespaldo(r.archivo)} className="text-emerald-600 hover:underline">Ver</button>
                    )}
                    <button onClick={() => handleDelete(r.id)} className="text-rose-500 hover:text-rose-700">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
